// AggregationSummary — surfaces detection-window facts from a rule's
// raw_event_json (mailbox owner, delegate, window, folders, IPs, client info).
// Generic over the snake_case schema the detection pipeline emits, so any
// aggregation rule that ships these fields gets a "who/when/what" header for
// free without per-rule wiring. Renders nothing for non-aggregation events.
//
// Used by AlertsPage and TestRunResultsPage expanded rows.

import React, { useMemo } from 'react';
import CopyButton from '../CopyButton';
import { parseAggregationFields } from './aggregationHelpers';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';

// Inline label-left detail field shape — matches AlertsPage's local DetailField
// (label sits inline left in a fixed w-24 column, value to the right). The
// shared sharedHelpers.DetailField is a stacked variant intended for the
// per-log_type Source Event grid, so it isn't substitutable here.
function DetailField({ label, children, mono = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-500 shrink-0 w-24">{label}</span>
      <span className={`text-gray-300 break-all ${mono ? 'font-mono' : ''}`}>{children || '—'}</span>
    </div>
  );
}

export default function AggregationSummary({ rawEventJson, tzIana }) {
  const agg = useMemo(() => parseAggregationFields(rawEventJson), [rawEventJson]);
  if (!agg) return null;

  const owner = agg.mailbox_owner_upn ||
    (Array.isArray(agg.set_MailboxOwnerUPN) && agg.set_MailboxOwnerUPN.length > 0
      ? agg.set_MailboxOwnerUPN.join(', ') : null);
  const delegateUpn = agg.delegate_upn || null;
  const delegateStatus = agg.delegate_resolution_status || null;
  const delegateSessionIds = Array.isArray(agg.delegate_aad_session_ids) ? agg.delegate_aad_session_ids : [];
  const showDelegate = !!(delegateStatus && delegateStatus !== 'skipped');
  const winStart = agg.detection_window_start;
  const winEnd = agg.detection_window_end;
  const accessCount = agg.access_count;
  const folderCount = agg.folder_count;
  const folders = Array.isArray(agg.distinct_folders) ? agg.distinct_folders : null;
  const ips = Array.isArray(agg.set_ClientIP) ? agg.set_ClientIP : null;
  const clientInfos = Array.isArray(agg.set_ClientInfoString) ? agg.set_ClientInfoString : null;

  // Aggregation events have ISO-8601 strings without a TZ suffix — normalizeIsoUtc
  // appends 'Z' (Databricks writes UTC) and truncates sub-ms precision so
  // Firefox/Safari don't Invalid-Date it; resolveTz maps the "Local" empty iana
  // to the browser zone (an empty timeZone throws). See feedback-local-tz-empty-iana.
  const fmtTs = (ts) => {
    if (!ts) return null;
    try {
      return new Date(normalizeIsoUtc(ts)).toLocaleString('en-US', {
        timeZone: resolveTz(tzIana), month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return ts; }
  };

  return (
    <div className="px-6 py-3 border-b border-gray-800/60">
      <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
        Aggregation Summary
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1">
        {owner && (
          <DetailField label="Mailbox Owner" mono>
            <span className="group inline-flex items-center gap-0.5">
              <span className="text-amber-300">{owner}</span>
              <CopyButton text={owner} />
            </span>
          </DetailField>
        )}
        {showDelegate && delegateUpn && (
          <DetailField label="Delegate" mono>
            <span className="group inline-flex items-center gap-0.5">
              <span className="text-sky-300">{delegateUpn}</span>
              <CopyButton text={delegateUpn} />
            </span>
          </DetailField>
        )}
        {showDelegate && !delegateUpn && (
          <DetailField label="Delegate">
            <span className="text-amber-400 text-xs font-medium">Unresolved</span>
            {delegateSessionIds.length > 0 && (
              <span className="block text-[10px] text-gray-500 font-mono break-all mt-0.5">
                AAD session: {delegateSessionIds.join(', ')}
              </span>
            )}
          </DetailField>
        )}
        {(winStart || winEnd) && (
          <DetailField label="Detection Window">
            <span>{fmtTs(winStart) || '—'}{winEnd ? ` → ${fmtTs(winEnd)}` : ''}</span>
          </DetailField>
        )}
        {accessCount != null && (
          <DetailField label="Access Count">{accessCount}</DetailField>
        )}
        {folders && folders.length > 0 ? (
          <DetailField label={`Folders (${folders.length})`} mono>
            <span className="break-all">{folders.join(', ')}</span>
          </DetailField>
        ) : folderCount != null ? (
          <DetailField label="Folder Count">{folderCount}</DetailField>
        ) : null}
        {ips && ips.length > 0 && (
          <DetailField label={`Source IPs (${ips.length})`} mono>
            <span className="break-all">{ips.join(', ')}</span>
          </DetailField>
        )}
        {clientInfos && clientInfos.length > 0 && (
          <DetailField label={`Client Info (${clientInfos.length})`}>
            <span className="break-all">{clientInfos.join(' | ')}</span>
          </DetailField>
        )}
      </div>
    </div>
  );
}
